import React, { useMemo } from 'react'
import styles from './rangeColorCircle.module.scss'

const DEFAULT_COLORS_3: string[] = [
  'rgb(240, 66, 66)',
  'rgb(231, 176, 8)',
  'rgb(33, 196, 93)',
]
const DEFAULT_COLORS_4: string[] = [
  'rgb(240, 66, 66)',
  'rgb(249, 116, 21)',
  'rgb(231, 176, 8)',
  'rgb(33, 196, 93)',
]

function getDefaultColors(rangeCount: number): string[] {
  if (rangeCount === 3) return DEFAULT_COLORS_3
  if (rangeCount === 4) return DEFAULT_COLORS_4
  return Array(rangeCount).fill('rgb(158, 158, 158)')
}

/** Range [min, max] (inclusive) for each segment. Value falls into one range to determine color. */
export type BlockRange = [number, number]

export interface RangeColorCircleProps {
  /** Optional className for the root element */
  className?: string
  /** Diameter of the circle in px (default 24) */
  size?: number
  /** Current value – used with ranges to determine which color to show */
  value: number
  /** Range [min, max] per segment (inclusive). Length = number of segments. */
  ranges: BlockRange[]
  /** Color per range. Optional: defaults to 3 or 4 preset colors when ranges length is 3 or 4. */
  colors?: string[]
  /** Optional aria-label for accessibility */
  'aria-label'?: string
}

function getSelectedIndex(value: number, ranges: BlockRange[]): number {
  const rounded = Math.round(value)
  const index = ranges.findIndex(([min, max]) => rounded >= min && rounded <= max)
  if (index >= 0) return index
  if (rounded < ranges[0][0]) return 0
  return ranges.length - 1
}

/**
 * Circle indicator that shows a single color based on value and ranges.
 * Uses the same range/color logic as SegmentedProgressSteps (red / orange / yellow / green).
 */
const RangeColorCircle: React.FC<RangeColorCircleProps> = (props) => {
  const {
    className,
    size = 24,
    value,
    ranges,
    colors,
    'aria-label': ariaLabel,
  } = props

  const rangeCount = ranges.length
  const effectiveColors =
    colors && colors.length === rangeCount ? colors : getDefaultColors(rangeCount)

  const selectedIndex = useMemo(() => getSelectedIndex(value, ranges), [value, ranges])
  const backgroundColor = effectiveColors[selectedIndex] ?? effectiveColors[0]

  return (
    <span
      className={`${styles.root} ${className ?? ''}`.trim()}
      style={{
        width: size,
        height: size,
        backgroundColor,
      }}
      role="img"
      aria-label={ariaLabel ?? `Range indicator: value ${value}`}
      data-testid="range-color-circle"
      data-value={value}
      data-range-index={selectedIndex}
    />
  )
}

export default RangeColorCircle
